import React from 'react';
import '../EvaluationDesign.css';

const AXIS_MAX = 4;
const THRESHOLD = 2.5;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const normalizeText = (value) => (value || '').toString().trim().toUpperCase();
const displayName = (value) => {
    if (!value || typeof value !== 'object') return value || '';
    return value.nom || value.name || value.libelle || value.label || value.code || '';
};
const typeValue = (value) => typeof value === 'object' ? displayName(value) : value;

const getLevelScore = (level) =>
    Number(level?.scoreObtenu ?? level?.scoreRf ?? level?.scoreN2 ?? level?.scoreManager ?? level?.scoreCollab ?? 0);

const getSkillType = (level) =>
    normalizeText(
        typeValue(level?.skillType)
        || level?.skillTypeNom
        || level?.typeCompetence
        || typeValue(level?.type)
        || level?.category
        || typeValue(level?.skill?.type)
        || typeValue(level?.skill?.skillType)
    );

const average = (levels) => {
    const scores = (levels || []).map(getLevelScore).filter((score) => Number.isFinite(score));
    if (!scores.length) return 0;
    return scores.reduce((sum, score) => sum + score, 0) / scores.length;
};

const averageByType = (levels, typeMatcher) => {
    const typedLevels = (levels || []).filter((level) => typeMatcher(getSkillType(level)));
    return typedLevels.length ? average(typedLevels) : null;
};

const getInitials = (name) =>
    (name || '??')
        .trim()
        .split(/\s+/)
        .map((part) => part[0] || '')
        .join('')
        .toUpperCase()
        .substring(0, 2) || '??';

const getQuadrant = (softScore, metierScore) => {
    if (softScore >= THRESHOLD && metierScore >= THRESHOLD) return 'star';
    if (softScore < THRESHOLD && metierScore >= THRESHOLD) return 'developper';
    if (softScore >= THRESHOLD && metierScore < THRESHOLD) return 'experts';
    return 'repositionner';
};

// Mappe un score [0, AXIS_MAX] vers un pourcentage dans la zone utile du plot
// lo/hi définissent les bornes sûres pour que les points restent entre les axes
const toPercent = (score, axis) => {
    const lo = 10;
    const hi = axis === 'x' ? 88 : 86;
    const ratio = Math.min(1, Math.max(0, score / AXIS_MAX));
    return lo + ratio * (hi - lo);
};

const QuadrantLabel = ({ className, title, count }) => (
    <div className={`eval-team4box-quadrant-label ${className}`}>
        <strong>{title}</strong>
        <span>{count}</span>
    </div>
);

const Team4Box = ({ teamData, onUserSelect, compact = false }) => {
    const processedData = (teamData || []).map((user, index) => {
        const levels = user.niveaux || [];
        if (levels.length === 0) return null;

        const globalAverage = average(levels);
        const softScore = averageByType(levels, (type) => type.includes('SOFT')) ?? globalAverage;
        const metierScore = averageByType(levels, (type) => type.includes('METIER') || type.includes('MÉTIER')) ?? globalAverage;
        const name = user.userFullName || user.nom || 'Collaborateur';
        const quadrant = getQuadrant(softScore, metierScore);

        // Léger jitter pour éviter la superposition exacte
        const jitterX = ((index % 5) - 2) * 0.6;
        const jitterY = ((Math.floor(index / 5) % 5) - 2) * 0.6;

        return {
            id: user.id || `${name}-${index}`,
            name,
            initials: user.initiales || getInitials(name),
            softScore,
            metierScore,
            quadrant,
            userObj: user,
            // clamp resserré : les points restent bien entre les deux flèches
            left: clamp(toPercent(softScore, 'x') + jitterX, 12, 86),
            bottom: clamp(toPercent(metierScore, 'y') + jitterY, 14, 84),
        };
    }).filter(Boolean);

    if (processedData.length === 0) {
        return (
            <div className="eval-empty">
                <h3>Matrice d'équipe</h3>
                <p>Aucune donnée disponible pour projeter l'équipe.</p>
            </div>
        );
    }

    const counts = processedData.reduce((acc, user) => {
        acc[user.quadrant] = (acc[user.quadrant] || 0) + 1;
        return acc;
    }, {});

    return (
        <div className={`eval-team4box${compact ? ' compact' : ''}`}>
            {/* Label axe Y — collé au plot via CSS */}
            <div className="eval-team4box-axis-label y-axis">Métier</div>

            <div className="eval-team4box-plot" style={{ height: compact ? 300 : 440 }}>
                {/* Axes avec flèches */}
                <div className="eval-team4box-axis x-axis" />
                <div className="eval-team4box-axis y-axis" />

                {/* Lignes médianes */}
                <div className="eval-team4box-midline horizontal" />
                <div className="eval-team4box-midline vertical" />

                {/* Labels quadrants */}
                <QuadrantLabel className="top-left"    title="À développer"    count={counts.developper    || 0} />
                <QuadrantLabel className="top-right"   title="Star"            count={counts.star          || 0} />
                <QuadrantLabel className="bottom-left" title="À repositionner" count={counts.repositionner || 0} />
                <QuadrantLabel className="bottom-right" title="Experts"        count={counts.experts       || 0} />

                {/* Points collaborateurs */}
                {processedData.map((user) => (
                    <button
                        key={user.id}
                        type="button"
                        className={`eval-team4box-point ${user.quadrant}`}
                        style={{ left: `${user.left}%`, bottom: `${user.bottom}%` }}
                        onClick={() => onUserSelect && onUserSelect(user.userObj)}
                        title={`${user.name} | Soft skills: ${user.softScore.toFixed(1)}/4 | Métier: ${user.metierScore.toFixed(1)}/4`}
                    >
                        <span>{user.initials}</span>
                    </button>
                ))}

                {/* Label axe X — à l'intérieur du plot, juste sous la flèche */}
                <div className="eval-team4box-axis-label x-axis">Soft skills</div>
            </div>
        </div>
    );
};

export default Team4Box;